import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  loadEcardDraft,
  loadEcardAccount,
  saveEcardAccount,
  clearEcardAccount,
} from "../constants/giftJourney";
import { clearAuthSession, saveAuthSession } from "../constants/authSession";
import { auth, db } from "../api/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { ref, set, get, serverTimestamp } from "firebase/database";
import "../styles/journey.css";
import "../styles/productDetails.css";

function isEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
}

function isFourDigitPin(v) {
  return /^\d{4}$/.test(String(v || ""));
}

function pinToPassword(pin) {
  return `giftekart-pin-${String(pin)}`;
}

export default function ProductAccount() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [formInfo, setFormInfo] = useState("");
  const activeEmail = String(auth?.currentUser?.email || "").trim().toLowerCase();
  const [showManualForm, setShowManualForm] = useState(!activeEmail);

  useEffect(() => {
    const draft = loadEcardDraft(id);
    if (!draft || !draft.recipientName) {
      navigate(`/product/${id}/customize`, { replace: true });
      return;
    }
    const account = loadEcardAccount(id);
    if (account?.email) {
      setEmail(account.email);
    }
  }, [id, navigate]);

  const handleNext = async (e) => {
    e.preventDefault();
    setFormErr("");
    setFormInfo("");

    if (!isEmail(email)) {
      setFormErr("Please enter a valid email address.");
      return;
    }
    if (!isFourDigitPin(pin)) {
      setFormErr("PIN must be exactly 4 digits.");
      return;
    }

    setBusy(true);
    try {
      if (!auth) {
        throw new Error("Account service is temporarily unavailable.");
      }
      if (!db) {
        throw new Error("Account service is temporarily unavailable.");
      }
      const password = pinToPassword(pin);
      try {
        await createUserWithEmailAndPassword(auth, email.trim(), password);
      } catch (e) {
        if (e?.code === "auth/email-already-in-use") {
          setFormInfo("This email is already registered. Logging you in...");
          await signInWithEmailAndPassword(auth, email.trim(), password);
        } else {
          throw e;
        }
      }
      const uid = auth.currentUser?.uid;
      if (!uid) {
        throw new Error("Could not read authenticated user id.");
      }
      const profileRef = ref(db, `users/${uid}/profile`);
      const snap = await get(profileRef);
      const nowIso = new Date().toISOString();
      const base = {
        uid,
        email: email.trim().toLowerCase(),
        authProvider: "password",
        updatedAt: nowIso,
        updatedAtServer: serverTimestamp(),
      };
      if (snap.exists()) {
        await set(profileRef, {
          ...snap.val(),
          ...base,
          lastLoginAt: nowIso,
          lastLoginAtServer: serverTimestamp(),
        });
      } else {
        await set(profileRef, {
          ...base,
          createdAt: nowIso,
          createdAtServer: serverTimestamp(),
          lastLoginAt: nowIso,
          lastLoginAtServer: serverTimestamp(),
        });
      }
      saveEcardAccount(id, { email });
      saveAuthSession({ email });
      navigate(`/product/${id}/checkout`);
    } catch (err) {
      setFormErr(err?.message || "Could not create your account right now.");
    } finally {
      setBusy(false);
    }
  };

  const continueWithActive = async () => {
    if (!activeEmail) return;
    saveEcardAccount(id, { email: activeEmail });
    saveAuthSession({ email: activeEmail });
    navigate(`/product/${id}/checkout`);
  };

  const useAnotherAccount = async () => {
    setFormErr("");
    setFormInfo("");
    try {
      if (auth?.currentUser) {
        await signOut(auth);
      }
      clearAuthSession();
      clearEcardAccount(id);
      setPin("");
      setEmail("");
      setShowManualForm(true);
    } catch (e) {
      setFormErr(e?.message || "Could not switch account right now.");
    }
  };

  return (
    <div className="pd-wrap">
      <div className="journey-panel journey-customize journey-customize--form-only">
        <div className="journey-customize-head">
          <Link to={`/product/${id}/customize`} className="journey-link-back">
            ← Back to card details
          </Link>
          <h1>Create your account</h1>
          <p>
            Add your email and a 4-digit PIN to save this card and access future cards quickly.
          </p>
          <p className="journey-hint-small" style={{ marginTop: "-0.3rem" }}>
            Already have an account?{" "}
            <Link to="/login" state={{ nextPath: `/product/${id}/checkout` }}>
              Login here
            </Link>
            .
          </p>
          {activeEmail ? (
            <div className="journey-summary-card" style={{ marginTop: "0.9rem" }}>
              <p className="journey-price-line" style={{ marginTop: 0 }}>
                Logged in as <strong>{activeEmail}</strong>
              </p>
              <div className="journey-form-actions pd-customize-actions">
                <button type="button" className="pd-btn pd-btn-pay" onClick={continueWithActive}>
                  Continue with this account
                </button>
                <button type="button" className="pd-btn pd-btn-schedule" onClick={useAnotherAccount}>
                  Use another account
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {showManualForm ? (
          <form className="journey-form journey-form--wide" onSubmit={handleNext}>
            <label className="journey-field">
              <span>Email *</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </label>

            <label className="journey-field">
              <span>4-digit PIN *</span>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="••••"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </label>

            <p className="journey-hint-small">
              Tip: Pick a memorable PIN. You will need it for future logins on this email.
            </p>

            {formErr ? <p className="pd-error journey-inline-err">{formErr}</p> : null}
            {formInfo ? <p className="pd-success">{formInfo}</p> : null}

            <div className="journey-form-actions pd-customize-actions">
              <button type="submit" className="pd-btn pd-btn-pay" disabled={busy}>
                {busy ? "Saving account…" : "Next: review & pay →"}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}
